import { useCallback, useEffect, useRef, useState } from 'react';

export interface FillHover {
  ref: React.RefObject<HTMLDivElement | null>;
  rect: DOMRect | undefined;
  visible: boolean;
  triggerProps: {
    onMouseOver: () => void;
    onMouseLeave: () => void;
    onTouchStart: () => void;
    onTouchEnd: () => void;
    onTouchCancel: () => void;
  };
  popupProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

const HIDE_DELAY_MS = 150;

export function useFillHover(): FillHover {
  const ref = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DOMRect | undefined>();
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);

  const clearHideTimer = () => {
    if (hideTimer.current !== undefined) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = undefined;
    }
  };

  const show = useCallback(() => {
    clearHideTimer();
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setVisible(true);
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimer.current = window.setTimeout(() => setVisible(false), HIDE_DELAY_MS);
  }, []);

  const hideImmediately = useCallback(() => {
    clearHideTimer();
    setVisible(false);
  }, []);

  useEffect(() => () => clearHideTimer(), []);

  return {
    ref,
    rect,
    visible,
    triggerProps: {
      onMouseOver: show,
      onMouseLeave: scheduleHide,
      onTouchStart: show,
      onTouchEnd: hideImmediately,
      onTouchCancel: hideImmediately,
    },
    popupProps: {
      onMouseEnter: show,
      onMouseLeave: scheduleHide,
    },
  };
}
